import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api, ApiError } from "../client";
import {
  AuthMeResponse,
  LoginRequest,
  SignupRequest,
  type LoginRequest as LoginRequestT,
  type SignupRequest as SignupRequestT,
} from "../schemas";
import { qk } from "../queryKeys";

/**
 * useSession — current authenticated user (or null if signed out).
 *
 * Implementation note: the backend returns 401 to /api/auth/me when no
 * valid session cookie is present. ApiError surfaces that as status=401;
 * we map it to a stable `null` rather than letting the query stay in an
 * `error` state, because "signed out" is a normal app state, not a fault.
 *
 * Stale window is short (5s) so signup/login/logout flows feel instant —
 * the mutations invalidate this key, and the next render sees the new
 * session immediately.
 */
export function useSession() {
  return useQuery({
    queryKey: qk.authMe,
    queryFn: async () => {
      try {
        const data = await api.get("/api/auth/me", AuthMeResponse);
        return data.user;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 5_000,
    retry: false,
  });
}

export function useSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SignupRequestT) =>
      api.post("/api/auth/signup", SignupRequest.parse(input), AuthMeResponse),
    onSuccess: (data) => {
      qc.setQueryData(qk.authMe, data.user);
    },
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginRequestT) =>
      api.post("/api/auth/login", LoginRequest.parse(input), AuthMeResponse),
    onSuccess: (data) => {
      qc.setQueryData(qk.authMe, data.user);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/api/auth/logout", undefined, z.unknown()),
    onSuccess: () => {
      qc.setQueryData(qk.authMe, null);
      // After logout, invalidate any cached data that was scoped to the
      // previous user so a fresh login sees a clean slate.
      qc.invalidateQueries({ queryKey: qk.projects });
    },
  });
}
